import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Sparkles, LayoutDashboard, Settings, LogOut, Menu, X, Zap, Star, Coins, Home } from 'lucide-react'
import ProEditor from '../components/editor/ProEditor'
import ExtensionBanner from '../components/dashboard/ExtensionBanner'
import SubscriptionBanner from '../components/SubscriptionBanner'
import NotificationCenter from '../components/NotificationCenter'
import { NotificationProvider } from '../contexts/NotificationContext'
import UpgradeModal from '../components/UpgradeModal'
import LifetimeCheckoutModal from '../components/LifetimeCheckoutModal'
import TopUpModal from '../components/TopUpModal'
import FeedbackWidget from '../components/FeedbackWidget'
import VoiceGuide from '../components/VoiceGuide'
import { signOut, getUser, isProUser, getSubscriptionDetails, getSubscriptionStatus } from '../utils/auth'

export default function EditorPage() {
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [isPro, setIsPro] = useState(false)
    const [subscriptionStatus, setSubscriptionStatus] = useState({ status: 'free', daysRemaining: null })
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [showLifetimeModal, setShowLifetimeModal] = useState(false)
    const [showTopUpModal, setShowTopUpModal] = useState(false)

    useEffect(() => {
        const fetchUser = async () => {
            const currentUser = await getUser()
            setUser(currentUser)
            if (currentUser) {
                const proStatus = await isProUser(currentUser.id)
                setIsPro(proStatus)

                // Fetch subscription status for expiration warnings
                const subDetails = await getSubscriptionDetails(currentUser.id, currentUser.email)
                if (subDetails) {
                    setSubscriptionStatus(getSubscriptionStatus(subDetails))
                }
            }
        }
        fetchUser()
    }, [])

    const handleSignOut = async () => {
        await signOut()
        navigate('/')
    }

    return (
        <NotificationProvider user={user} subscriptionStatus={subscriptionStatus}>
            <div className="min-h-screen bg-slate-950 flex">
                {/* Sidebar */}
                <aside
                    className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 lg:static
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
                >
                    <div className="flex flex-col h-full">
                        {/* Logo */}
                        <div className="flex items-center gap-2 px-6 py-5 border-b border-white/10">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-white">InkSpot</span>
                            {isPro && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-full">
                                    PRO
                                </span>
                            )}
                        </div>

                        {/* Nav Links */}
                        <nav className="flex-grow px-4 py-6">
                            <ul className="space-y-2">
                                <li>
                                    <Link
                                        to="/"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                    >
                                        <Home className="w-5 h-5" />
                                        Home
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/app"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 text-white font-medium"
                                    >
                                        <Sparkles className="w-5 h-5" />
                                        Editor
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/dashboard"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                    >
                                        <LayoutDashboard className="w-5 h-5" />
                                        Dashboard
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/settings"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                    >
                                        <Settings className="w-5 h-5" />
                                        Settings
                                    </Link>
                                </li>
                            </ul>
                        </nav>

                        {/* User Section */}
                        <div className="p-4 border-t border-white/10">
                            <div className="flex items-center gap-3 px-2 mb-4">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center text-white font-bold">
                                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div className="flex-grow min-w-0">
                                    <p className="text-sm font-medium text-white truncate">
                                        {user?.email || 'User'}
                                    </p>
                                    <p className="text-xs text-white/50">
                                        {isPro ? 'Pro Plan' : 'Free Plan'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleSignOut}
                                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                            >
                                <LogOut className="w-5 h-5" />
                                Sign out
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Mobile Sidebar Overlay */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <main className="flex-grow flex flex-col min-h-screen">
                    {/* Mobile Header */}
                    <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="p-2 text-white/70 hover:text-white"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary-400" />
                            <span className="font-bold text-white">InkSpot</span>
                        </div>
                        <NotificationCenter />
                    </header>

                    {/* Editor Content */}
                    <div className="flex-grow p-4 lg:p-8">
                        <div className="max-w-7xl mx-auto">
                            {/* Desktop Header with Notifications */}
                            <div className="hidden lg:flex items-center justify-end mb-4">
                                <NotificationCenter />
                            </div>

                            {/* Page Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                                <div>
                                    <h1 className="text-2xl font-bold text-white mb-2">
                                        Writing Editor
                                    </h1>
                                    <p className="text-white/60">
                                        Paste or type your text below to detect clichés and get AI-powered suggestions.
                                    </p>
                                </div>
                                {!isPro && (
                                    <button
                                        onClick={() => setShowUpgradeModal(true)}
                                        className="btn-primary flex items-center gap-2 whitespace-nowrap"
                                    >
                                        <Zap className="w-4 h-4" />
                                        Upgrade to Pro
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowTopUpModal(true)}
                                    className="flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white hover:bg-white/15 transition-colors font-medium"
                                >
                                    <Coins className="w-4 h-4" />
                                    Buy Credits
                                </button>
                            </div>

                            {/* Subscription Expiration Banner */}
                            <SubscriptionBanner 
                                subscriptionStatus={subscriptionStatus} 
                                isPro={isPro} 
                                onRenew={() => setShowUpgradeModal(true)}
                            />

                            {/* Chrome Extension Banner */}
                            <ExtensionBanner isPro={isPro} />

                            {/* Editor Component */}
                            <div className="min-h-[calc(100vh-300px)]">
                                <ProEditor />
                            </div>

                            {/* Upgrade Modal */}
                            <UpgradeModal
                                isOpen={showUpgradeModal}
                                onClose={() => setShowUpgradeModal(false)}
                            />
                            {/* Top Up Credits Modal */}
                            <TopUpModal
                                isOpen={showTopUpModal}
                                onClose={() => setShowTopUpModal(false)}
                                userEmail={user?.email}
                            />

                            {/* Lifetime Checkout Modal */}
                            <LifetimeCheckoutModal
                                isOpen={showLifetimeModal}
                                onClose={() => setShowLifetimeModal(false)}
                                userEmail={user?.email}
                            />

                            {/* Founding Member Lifetime Deal (for free users) */}
                            {!isPro && (
                                <div className="glass-card p-6 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 mt-6">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                                                <Star className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white mb-1">
                                                    🔥 Founding Member Deal
                                                </h3>
                                                <p className="text-white/60">
                                                Pay $29 once for lifetime Pro access. Limited to 100 members!
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setShowLifetimeModal(true)}
                                            className="whitespace-nowrap px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                                        >
                                            <Star className="w-4 h-4" />
                                            Get Lifetime Access
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                {/* Feedback Widget */}
                <FeedbackWidget user={user} />

                {/* Voice Guide Assistant */}
                <VoiceGuide />
            </div>
        </NotificationProvider>
    )
}
